import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEnter, useReveal } from "@/lib/motion";
import { FOUNDER, CONTACT, CLIENTS, STUDIO } from "@/lib/siteData";
import { usePageMeta } from "@/lib/pageMeta";
import { AutoScroller } from "@/components/AutoScroller";
import { FounderGallerySlider } from "@/components/founder/FounderGallerySlider";

// Exact 4 best cases in the designer's requested order:
const BEST_CASE_SLUGS = [
    "weheal-ftw",
    "debora-miranda",
    "andreetta-contabilidade",
    "samir-el-hage",
];

const bestCases = BEST_CASE_SLUGS.map((slug) =>
    CLIENTS.find((client) => client.slug === slug)
).filter(Boolean);

// The 3 socials shown in the design:
const SOCIALS = [
    { name: "Instagram", href: CONTACT.instagram.href },
    { name: "LinkedIn", href: CONTACT.linkedin.href },
    { name: "Behance", href: CONTACT.behance.href },
];

export const FounderPage = () => {
    usePageMeta({
        title: `${FOUNDER.name} — ${FOUNDER.role}`,
        description: FOUNDER.quote,
        path: "/sobre/andre",
    });

    const enter = useEnter();
    const reveal = useReveal();

    return (
        <section className="bg-paper pb-24">
            {/* Top Grid: Portrait + Info & Bio + Best Cases */}
            <div className="mx-auto grid max-w-[1440px] px-6 md:px-12 lg:grid-cols-[40%_minmax(0,1fr)] lg:gap-14 lg:px-16">
                {/* Left Column: Portrait + Founder info & Socials (flush with header) */}
                <div className="flex flex-col">
                    <motion.div
                        className="relative aspect-[3/4] w-full overflow-hidden rounded-t-none rounded-br-[80px] md:rounded-br-[100px] bg-slot shadow-sm"
                        {...enter()}
                    >
                        <img
                            src={FOUNDER.photo}
                            alt={FOUNDER.name}
                            className="h-full w-full object-cover"
                            style={{ objectPosition: "center 30%" }}
                        />
                    </motion.div>

                    {/* Aligned directly under the portrait */}
                    <motion.div
                        className="mt-8 flex items-start justify-between gap-6"
                        {...reveal(0.1)}
                    >
                        <div>
                            <p className="type-display text-[22px] font-bold text-ink">Fundador</p>
                            <img
                                src="/images/brand/logo-principal-colorida.png"
                                alt={STUDIO.name}
                                className="mt-3 h-[58px] md:h-[68px] w-auto"
                            />
                        </div>

                        <nav aria-label="Redes sociais do fundador" className="pt-1">
                            <ul className="flex flex-col gap-2.5">
                                {SOCIALS.map((social) => (
                                    <li key={social.name}>
                                        <a
                                            href={social.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="type-display text-[19px] font-bold text-brand transition-opacity duration-200 hover:opacity-75"
                                        >
                                            {social.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </motion.div>
                </div>

                {/* Right Column: Title, Bio, Education & Best Cases */}
                <div className="min-w-0 pt-8 sm:pt-10 lg:pt-10">
                    <motion.h1
                        className="type-display text-[clamp(36px,4.5vw,56px)] font-bold leading-tight text-ink"
                        {...enter()}
                    >
                        {FOUNDER.name}
                    </motion.h1>

                    {/* Bio paragraphs */}
                    {FOUNDER.bio ? (
                        <motion.div
                            className="mt-6 max-w-[640px] space-y-4 text-[15px] md:text-[16px] leading-relaxed text-ink/85"
                            {...enter(0.06)}
                        >
                            {Array.isArray(FOUNDER.bio) ? (
                                FOUNDER.bio.map((paragraph, index) => (
                                    <p key={index}>{paragraph}</p>
                                ))
                            ) : (
                                <p>{FOUNDER.bio}</p>
                            )}
                        </motion.div>
                    ) : null}

                    {/* Education */}
                    <motion.dl
                        className="mt-10 grid max-w-[640px] grid-cols-2 gap-x-8 gap-y-4 text-center"
                        {...enter(0.12)}
                    >
                        {FOUNDER.education.map((entry) => (
                            <div key={entry.school}>
                                <dt className="text-[16px] font-bold text-ink">{entry.school}</dt>
                                <dd className="mt-1 text-[14px] text-ink/80">{entry.course}</dd>
                            </div>
                        ))}
                    </motion.dl>

                    {/* Bests Cases */}
                    <motion.div className="mt-12" {...reveal(0.16)}>
                        <h2 className="type-display text-[clamp(24px,2.4vw,30px)] font-bold text-ink">
                            Bests Cases
                        </h2>

                        <div className="mt-6">
                            <AutoScroller ariaLabel="Melhores cases">
                                {bestCases.map((client) => (
                                    <Link
                                        key={client.slug}
                                        to={`/cases/${client.slug}`}
                                        className="group w-[172px] shrink-0"
                                    >
                                        <span className="block aspect-[172/260] overflow-hidden rounded-[18px] bg-slot shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md">
                                            {client.cover ? (
                                                <img
                                                    src={client.cover}
                                                    alt={client.name}
                                                    className="h-full w-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : null}
                                        </span>
                                        <span className="mt-3 block text-[15px] font-bold text-ink group-hover:text-brand transition-colors">
                                            {client.name}
                                        </span>
                                    </Link>
                                ))}
                            </AutoScroller>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Bottom Gallery Carousel with Center-Popped 3D Effect */}
            <motion.div className="mt-16 md:mt-24 overflow-hidden" {...reveal()}>
                <FounderGallerySlider items={FOUNDER.gallery} />

                {/* Bottom CTA Button */}
                <div className="mt-10 md:mt-14 flex justify-center px-6">
                    <Link
                        to="/contato"
                        className="inline-flex items-center justify-center bg-brand hover:bg-brand-deep text-white font-bold uppercase tracking-wider text-[14px] sm:text-[15px] px-8 sm:px-10 py-3.5 sm:py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                    >
                        SOLICITE UM ORÇAMENTO!
                    </Link>
                </div>
            </motion.div>
        </section>
    );
};
